import { ReactNode } from 'react'

type GuideStep = {
  /** アンカー。/guide#b-2 のように手順を直接指せる */
  id: string
  /** 見出しの文言そのまま。見出しは事象だけなので、そのまま目次に出せる */
  title: string
}

export type GuideSection = {
  /** アンカー。/guide#a のように紙・CRC台本から直接指せる */
  id: string
  /** A〜D の区分記号。紙のシートと逐語一致させる */
  label?: string
  /** 目次・見出しに出す文言 */
  title: string
  body: string
  /** 本文の ### 見出しから機械的に起こした手順一覧（目次の2階層目） */
  steps: GuideStep[]
}

/**
 * 系統別variant（variants/ の各ファイルが1系統ずつ定義する）。
 * コンテンツは系統別に静的に持ち、入口（紙のQR・PDF）で振り分ける。
 * /guide は未認証公開ページで閲覧者を判定する手段が無いため、動的分岐はしない。
 */
export type GuideVariant = {
  sections: GuideSection[]
  /** 「それでも解決しないとき」冒頭の連絡先。系統ごとに違うため md ではなく variant が持つ */
  contact: ReactNode
}

/**
 * 見出しの文言からアンカーを導く。
 *
 * 目次と本文で id を突き合わせる必要があるが、文言を variant 定義と md に
 * 二重で持つと必ずずれる。md の見出しを唯一の出所とし、双方がこの関数で
 * 同じ id を導出する。出現順の連番には依存させない（StrictMode の二重
 * レンダで狂うため）。
 */
export const headingId = (heading: string): string => {
  const step = heading.match(/^([A-D])-(\d+)\./)
  if (step) return `${step[1].toLowerCase()}-${step[2]}`

  const appendix = heading.match(/^付録(\d+)\./)
  if (appendix) return `apx-${appendix[1]}`

  return ''
}

/**
 * 本文の ### 見出しから手順一覧を起こす。
 *
 * 記号（A-1. / 付録1.）で始まらない見出しは id を持てないため、目次にも
 * アンカーにも出せない。ただし本文には出てしまうので、黙って落とすと
 * 「書いたのに目次に無い」に気づけない。目次がこのページの骨格である以上、
 * 落ちたことは開発時に見えていないといけない。
 */
const extractSteps = (body: string): GuideStep[] =>
  [...body.matchAll(/^### (.+)$/gm)]
    .map(([, heading]) => {
      const title = heading.trim()
      const id = headingId(title)
      if (!id && import.meta.env.DEV) {
        console.warn(
          `[guide] 見出し「${title}」は記号（A-1. / 付録1.）で始まらないため、目次にもアンカーにも出ません。`
        )
      }
      return { id, title }
    })
    .filter((step) => Boolean(step.id))

export const section = (s: Omit<GuideSection, 'steps'>): GuideSection => ({
  ...s,
  steps: extractSteps(s.body),
})
